import { createDraftSafeSelector } from '@reduxjs/toolkit';
import { compareDesc } from 'date-fns';
import {
  DispositionDocument,
  ExpiredDocument,
  Practice,
  PracticeDocument
} from '../../../../model';
import { Document } from '../../../../model/documents/document';
import { DocumentType } from '../../../../model/documents/document-type';
import { RootState } from '../../../root-store';
import { DocumentsState, selectDocumentsState } from '../documents-selectors';
import {
  DocumentsFilter,
  DocumentsListState,
} from './documents-list-types';

export const getDocumentsListState:any = createDraftSafeSelector(
  selectDocumentsState,
  (state: DocumentsState) => {
    return state.documentsList;
  }
);

export const getDocumentsListFilter = (state: RootState): DocumentsFilter =>
  (state.features.documents as DocumentsState)?.documentsList?.filter;

export const getDocumentsTypeFilters:any = createDraftSafeSelector(
  getDocumentsListState,
  (state: DocumentsListState) => state.filter.documentsFilters
);

export const getContracts:any = createDraftSafeSelector(
  getDocumentsListState,
  getDocumentsListFilter,
  (state: DocumentsListState, filter: DocumentsFilter) =>
    getDocumentList(state.contracts, filter)
);

export const getDispositions:any = createDraftSafeSelector(
  getDocumentsListState,
  getDocumentsListFilter,
  (state: DocumentsListState, filter: DocumentsFilter) =>
    getPracticeList<DispositionDocument>(state.dispositions, filter)
);

export const getExpiredDocuments:any = createDraftSafeSelector(
  getDocumentsListState,
  getDocumentsListFilter,
  (state: DocumentsListState, filter: DocumentsFilter) =>
    getPracticeList<ExpiredDocument>(state.expiredDocuments, filter)
);

export const isDocumentsListShown:any = createDraftSafeSelector(
  [getDocumentsListState, (_, type: DocumentType) => type],
  (state: DocumentsListState, type) => state.filter.documentsFilters[type].show
);

export const getUnreadDocumentsCount:any = createDraftSafeSelector(
  [getDocumentsListState, (_, type: DocumentType) => type],
  (state: DocumentsListState, type) =>
    state.filter.documentsFilters[type].unreadCount
);

export const getRequestStateForDocumentType:any = createDraftSafeSelector(
  [getDocumentsListState, (_: DocumentsListState, type: DocumentType) => type],
  (state: DocumentsListState, type: DocumentType) => {
    switch (type) {
      case 'Disposition':
        return state.dispositionsRequestStatus;
      case 'ExpiredDocuments':
        return state.expiredDocumentsRequestStatus;
      case 'Contract':
      default:
        return state.contractsRequestStatus;
    }
  }
);

/*type CompareMap = {
  [Key in DocumentsOrderCriteria]: (
    dateLeft: number | Date,
    dateRight: number | Date
  ) => number;
};
const compareMap: CompareMap = {
  UploadDate: compareAsc,
  Title: compareDesc,
};
*/
const sortFn = (
  a: Document,
  b: Document,
  filter: DocumentsFilter
): number => {
  if (filter.orderCriteria === 'Title') {
    return a.title.localeCompare(b.title);
  } else {
    return compareDesc(a.uploadDate, b.uploadDate);
  }
};

const sortFnPractice = <T extends PracticeDocument>(
  a: Practice<T>,
  b: Practice<T>,
  filter: DocumentsFilter
): number => {
  if (filter.orderCriteria === 'Title') {
    return a.documents[0].title.localeCompare(b.documents[0].title);
  } else {
    return compareDesc(a.documents[0].uploadDate, b.documents[0].uploadDate);
  }
};


const getDocumentList = (
  documents: Document[],
  filter: DocumentsFilter
): Document[] => {
  return documents
    ?.filter((document) => !filter?.showUnreadDocuments || !document.flagIsRead)
    .sort((a, b) => sortFn(a, b, filter));
};

const getPracticeList = <T extends PracticeDocument>(
  practices: Practice<T>[],
  filter: DocumentsFilter
): Practice<T>[] => {
  return practices
    ?.map((practice) => ({
      ...practice,
      documents: getDocumentList(practice.documents, filter) as T[],
    }))
    .filter((practice) => practice.documents?.length > 0)
    .sort((a, b) => sortFnPractice(a, b, filter));
};
